import { useState, useEffect, useCallback, RefObject } from 'react'

export interface TextSelection {
  text: string
  startOffset: number
  endOffset: number
  rect: DOMRect
}

export function useTextSelection(containerRef: RefObject<HTMLElement | null>) {
  const [selection, setSelection] = useState<TextSelection | null>(null)

  const getTextOffset = useCallback(
    (container: HTMLElement, node: Node, offset: number): number => {
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
      let totalOffset = 0

      while (walker.nextNode()) {
        if (walker.currentNode === node) {
          return totalOffset + offset
        }
        totalOffset += walker.currentNode.textContent?.length || 0
      }
      return totalOffset
    },
    []
  )

  const handleSelectionChange = useCallback(() => {
    const sel = window.getSelection()

    if (!sel || sel.isCollapsed || !sel.rangeCount || !containerRef.current) {
      setSelection(null)
      return
    }

    const range = sel.getRangeAt(0)
    const text = sel.toString().trim()

    // Minimum 3 characters to create a highlight
    if (!text || text.length < 3) {
      setSelection(null)
      return
    }

    // Check if selection is within our container
    if (!containerRef.current.contains(range.commonAncestorContainer)) {
      setSelection(null)
      return
    }

    const startOffset = getTextOffset(
      containerRef.current,
      range.startContainer,
      range.startOffset
    )
    const endOffset = startOffset + text.length

    setSelection({
      text,
      startOffset,
      endOffset,
      rect: range.getBoundingClientRect(),
    })
  }, [containerRef, getTextOffset])

  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange)
    document.addEventListener('mouseup', handleSelectionChange)
    document.addEventListener('touchend', handleSelectionChange)

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange)
      document.removeEventListener('mouseup', handleSelectionChange)
      document.removeEventListener('touchend', handleSelectionChange)
    }
  }, [handleSelectionChange])

  const clearSelection = useCallback(() => {
    window.getSelection()?.removeAllRanges()
    setSelection(null)
  }, [])

  return { selection, clearSelection }
}
